import { API } from "aws-amplify";
import { listLeases } from "~/graphql/queries";
import { GRAPHQLFilters, LoaderData } from "~/types";
import { getGraphqlFilters } from "~/utils";
import * as APITypes from "~/API";
import { FormattedLeaseData } from "~/types/lease";
import { EMPTY_UNIT, getUnitDetails } from "../../unit/getUnitDetails";
import { getParcelDetails } from "../../parcel/getParcelDetails";
import { EMPTY_TENANT_INFO, getTenantInfo } from "../../tenant";
import {
  EMPTY_LEASE_INFO,
  getOfferInfo,
  getLeaseInfo,
  getApplicationInfo,
  getBackgroundCheckInfo,
  getMoveExamInfo,
} from "../getLeaseInfo";

const EMPTY_LEASE: FormattedLeaseData = {
  leaseInfo: EMPTY_LEASE_INFO,
  tenantInfo: EMPTY_TENANT_INFO,
  entityType: APITypes.EntityType.Unit,
  entityDetails: EMPTY_UNIT,
  advanceLeaseAction: "_Review_Application",
  cancelLeaseAction: "_Deny_Application",
};

export const getLeaseUpdateList = async ({
  ownerID,
  ownerType,
  includeDeleted = "exclude",
}: {
  ownerID?: string;
  ownerType?: APITypes.OwnerType;
  includeDeleted?: "all" | "exclude" | "only";
}): Promise<LoaderData<FormattedLeaseData[]>> => {
  let filters: GRAPHQLFilters = getGraphqlFilters({
    ownerID,
    ownerType,
  });

  // Add _deleted filter based on includeDeleted parameter
  if (includeDeleted === "exclude") {
    filters._deleted = { ne: true };
  } else if (includeDeleted === "only") {
    filters._deleted = { eq: true };
  }
  // If includeDeleted === "all", don't add any _deleted filter

  try {
    let response = (await API.graphql({
      query: listLeases,
      variables: {
        filter: filters,
      },
    })) as { data?: APITypes.ListLeasesQuery };

    const allLeases =
      response.data?.listLeases?.items?.filter(
        (item): item is APITypes.Lease => item !== null
      ) ?? [];
    let filteredLease: FormattedLeaseData[] = [];
    for (let i = 0; i < allLeases.length; i++) {
      if (
        allLeases[i].status === APITypes.LeaseStatusType.Created &&
        allLeases[i].prevLeaseID?.length !== 0
      ) {
        filteredLease.push((await getFormattedLease(allLeases[i].id)).data);
      } else if (allLeases[i].status === APITypes.LeaseStatusType.Terminating) {
        filteredLease.push((await getFormattedLease(allLeases[i].id)).data);
      } else if (
        allLeases[i].status === APITypes.LeaseStatusType.UnderReview &&
        (await getOfferInfo(allLeases[i].offerID ?? "")).data.status !==
          APITypes.OfferStatusType.Completed
      ) {
        filteredLease.push((await getFormattedLease(allLeases[i].id)).data);
      }
    }
    return {
      success: true,
      data: filteredLease,
    };
  } catch (error) {
    console.error("Error getting lease list", error);
    return {
      success: false,
      data: [],
      error,
    };
  }
};

const getFormattedLease = async (
  leaseID: string
): Promise<LoaderData<FormattedLeaseData>> => {
  const leaseInfo = await getLeaseInfo(leaseID);
  if (leaseInfo.error) {
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get lease info failed", leaseInfo.error),
    };
  }

  var lease: FormattedLeaseData = {
    leaseInfo: leaseInfo.data,
    tenantInfo: EMPTY_TENANT_INFO,
    entityType: leaseInfo.data.entityType as APITypes.EntityType,
    entityDetails: EMPTY_UNIT,
    advanceLeaseAction: "_Review_Application",
    cancelLeaseAction: "_Deny_Application",
  };

  if (lease.entityType === APITypes.EntityType.Unit) {
    const unitDetails = await getUnitDetails(leaseInfo.data.entityID);
    lease.entityDetails = unitDetails.data;
  } else if (lease.entityType === APITypes.EntityType.Parcel) {
    const parcelDetails = await getParcelDetails(leaseInfo.data.entityID);
    lease.entityDetails = parcelDetails.data;
  }

  const tenant = await getTenantInfo({
    tenantID: leaseInfo.data.tenantID || "",
  });
  lease.tenantInfo = tenant.data;

  const application = await getApplicationInfo(
    leaseInfo.data.applicationID || ""
  );
  if (application.error) {
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get application info failed", application.error),
    };
  }

  lease.application = application.data;
  if (application.data.status === "UnderReview") {
    lease.advanceLeaseAction = "_Approve_Application";
  }

  const background = await getBackgroundCheckInfo(
    leaseInfo.data.backgroundCheckID || ""
  );
  if (background.error) {
    console.log("background ID", leaseInfo.data.backgroundCheckID);
    console.log("get background info failed", background.error);
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get background info failed", background.error),
    };
  } else {
    lease.background = background.data;

    if (background.data.status === "InProgress") {
      lease.advanceLeaseAction = "_Approve_Background";
      lease.cancelLeaseAction = "_Deny_Background";
    }

    if (background.data.status === "Approved") {
      lease.advanceLeaseAction = "_Send_Offer";
      lease.cancelLeaseAction = "_Cancel_Offer";
    }
  }

  const offer = await getOfferInfo(leaseInfo.data.offerID || "");
  if (offer.error) {
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get offer info failed", offer.error),
    };
  } else {
    lease.offer = offer.data;

    if (
      offer.data.status === APITypes.OfferStatusType.TenantComplete ||
      offer.data.status === "InProgress"
    ) {
      lease.advanceLeaseAction = "_Validate_Offer";
      lease.cancelLeaseAction = "_Cancel_Offer";
    }

    if (
      offer.data.status === "Completed" ||
      offer.data.status === APITypes.OfferStatusType.LandlordComplete
    ) {
      lease.advanceLeaseAction = "_Start_MoveIn";
      lease.cancelLeaseAction = "_Cancel_MoveIn";
    }
  }

  const moveIn = await getMoveExamInfo(leaseInfo.data.moveInID || "");
  if (moveIn.error) {
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get move in info failed", moveIn.error),
    };
  } else {
    lease.moveIn = moveIn.data;
    if (moveIn.data.status === "InProgress") {
      lease.advanceLeaseAction = "_Complete_MoveIn";
      lease.cancelLeaseAction = "_Cancel_MoveIn";
    }
  }

  const moveOut = await getMoveExamInfo(leaseInfo.data.moveOutID || "");
  if (moveOut.error) {
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get move out info failed", moveOut.error),
    };
  } else {
    lease.moveOut = moveOut.data;
    if (moveOut.data.status === "InProgress") {
      lease.advanceLeaseAction = "_Complete_MoveOut";
      lease.cancelLeaseAction = "_Cancel_MoveOut";
    }

    if (
      moveOut.data.status === "Completed" ||
      moveOut.data.status === "Failed"
    ) {
      lease.leaseInfo.status = APITypes.LeaseStatusType.Expired;
    }
  }

  return {
    success: true,
    data: lease,
  };
};
